import type { ControlProviderStore } from "@/context/provider-types";

export type {
  ControlProviderStore,
} from "@/context/provider-types";

import {
  createComponent,
  createContext,
  onCleanup,
  onMount,
  type ParentProps,
  useContext,
} from "solid-js";
import { sessionValues } from "@/storage/browser";
import { readUserSessionToken } from "@/storage/user-session";
import { decodeProviderStore, getControlProviders } from "@/transport/providers";
import { createQuery } from "@/utils/query";

export const popularProviders = [
  "openai",
  "anthropic",
  "github-copilot",
  "google",
  "xai",
  "opencode",
  "opencode-go",
  "openai-compatible",
];
const popularProviderSet = new Set(popularProviders);
const supportedProviderID =
  /^(?:openai-compatible|openai|anthropic|google|xai|github-copilot|opencode-go|opencode)(?:[2-9]|[1-9][0-9]+)?$/;

export function filterSupportedProviders<
  T extends {
    all: Array<{ id: string }>;
    connected: string[];
    default: Record<string, string>;
  },
>(value: T): T {
  const supported = (id: string) => supportedProviderID.test(id);
  return {
    ...value,
    all: value.all
      .filter((provider) => supported(provider.id))
      .map((provider) => ({
        ...provider,
        models: Object.fromEntries(
          Object.entries((provider as { models?: Record<string, { status?: string }> }).models ?? {}).filter(
            ([, model]) => model?.status !== "deprecated",
          ),
        ),
      })),
    connected: value.connected.filter(supported),
    default: Object.fromEntries(
      Object.entries(value.default).filter(([id]) => supported(id)),
    ),
  };
}

// Builtin providers ship with the runtime and hold no removable credentials,
// so offering "Disconnect" for them would always be a no-op.
// A stored credential can always be removed. "builtin" marks a provider the
// control plane lists before any credential exists, so it must not veto
// disconnect once the user has connected their own key.
export function providerCanDisconnect(provider: {
  source?: string;
  options?: Record<string, unknown>;
}): boolean {
  if (provider.source === "env") return false;
  const authType = provider.options?.authType;
  if (authType === "api" || authType === "oauth") return true;
  return provider.source !== "builtin";
}

const emptyProviders: ControlProviderStore = {
  all: [],
  default: {},
  connected: [],
};

const PROVIDER_STORAGE_MAX_KEYS = 40;
const controlProvidersStoragePrefix = "mux.control-providers.v2";
const providerStorageKeys = new Set<string>();

function trackProviderStorageKey(key: string) {
  providerStorageKeys.delete(key);
  providerStorageKeys.add(key);
  while (providerStorageKeys.size > PROVIDER_STORAGE_MAX_KEYS) {
    const stale = providerStorageKeys.keys().next().value;
    if (!stale) break;
    providerStorageKeys.delete(stale);
    sessionValues.removeItem(stale);
  }
}

function providerScope() {
  const token = readUserSessionToken();
  let scope = 2166136261;
  for (let index = 0; index < token.length; index++) {
    scope ^= token.charCodeAt(index);
    scope = Math.imul(scope, 16777619);
  }
  return (scope >>> 0).toString(36);
}

function controlProvidersStorageKey() {
  return `${controlProvidersStoragePrefix}:${providerScope()}`;
}

function readCachedControlProviders() {
  try {
    const key = controlProvidersStorageKey();
    const stored = sessionValues.getItem(key);
    if (!stored) return undefined;
    trackProviderStorageKey(key);
    return filterSupportedProviders(
      decodeProviderStore(JSON.parse(stored) as unknown),
    );
  } catch {
    return undefined;
  }
}

function writeCachedControlProviders(value: ControlProviderStore) {
  try {
    const key = controlProvidersStorageKey();
    trackProviderStorageKey(key);
    sessionValues.setItem(key, JSON.stringify(value));
  } catch {}
}

let cachedProviders: ControlProviderStore | undefined =
  readCachedControlProviders();
let inflightProviders: Promise<ControlProviderStore> | undefined;
let providersGeneration = 0;
let providersScope = readUserSessionToken();
let controlRequest:
  | { promise: Promise<ControlProviderStore>; controller: AbortController }
  | undefined;

function syncProvidersScope() {
  const scope = readUserSessionToken();
  if (scope === providersScope) return;
  providersScope = scope;
  providersGeneration++;
  cachedProviders = readCachedControlProviders();
  inflightProviders = undefined;
  controlRequest?.controller.abort();
  controlRequest = undefined;
}

export function clearProvidersCache() {
  providersScope = readUserSessionToken();
  providersGeneration++;
  cachedProviders = undefined;
  inflightProviders = undefined;
  controlRequest?.controller.abort();
  controlRequest = undefined;
  for (const key of providerStorageKeys) sessionValues.removeItem(key);
  providerStorageKeys.clear();
}

export function fetchControlProviders(options?: {
  force?: boolean;
  signal?: AbortSignal;
}): Promise<ControlProviderStore> {
  syncProvidersScope();
  if (!options?.force && cachedProviders)
    return Promise.resolve(cachedProviders);
  if (inflightProviders) return inflightProviders;
  if (options?.force) providersGeneration++;
  const generation = providersGeneration;
  const controller = new AbortController();
  const signal = options?.signal
    ? AbortSignal.any([controller.signal, options.signal])
    : controller.signal;
  const request = getControlProviders(signal)
    .then((value) => {
      const data = filterSupportedProviders(value);
      if (providersGeneration === generation) {
        cachedProviders = data;
        writeCachedControlProviders(data);
      }
      return data;
    })
    .finally(() => {
      if (inflightProviders === request) inflightProviders = undefined;
      if (controlRequest?.promise === request) controlRequest = undefined;
    });
  controlRequest = { promise: request, controller };
  inflightProviders = request;
  return request;
}

export function preloadProvidersData(options?: { signal?: AbortSignal }) {
  syncProvidersScope();
  if (cachedProviders || inflightProviders)
    return inflightProviders ?? Promise.resolve(cachedProviders!);
  return fetchControlProviders(options);
}

const refetchers = new Set<() => unknown>();

export function invalidateProviders() {
  clearProvidersCache();
  return Promise.all([...refetchers].map((refetch) => refetch()));
}

/** What consumers list and pick from in one inventory. */
function inventoryReads(inventory: () => ControlProviderStore) {
  const connected = () => {
    const ids = new Set([...inventory().connected, "opencode"]);
    return inventory().all.filter((p) => ids.has(p.id));
  };
  return {
    inventory,
    all: () => inventory().all,
    default: () => inventory().default,
    popular: () => inventory().all.filter((p) => popularProviderSet.has(p.id)),
    connected,
    paid: connected,
  };
}

function createAccountProviders() {
  syncProvidersScope();
  const providers = createQuery(
    () => true,
    () => fetchControlProviders({ force: true }),
    { initialValue: cachedProviders },
  );
  onMount(() => {
    const refetch = () => providers.refresh();
    refetchers.add(refetch);
    onCleanup(() => refetchers.delete(refetch));
  });
  const inventory = () => providers() ?? cachedProviders ?? emptyProviders;
  return {
    ...inventoryReads(inventory),
    get version() {
      return inventory().version ?? 0;
    },
    get loading() {
      return providers.loading && !cachedProviders && !providers();
    },
    get error() {
      return providers.error;
    },
    disconnectOptimistic(providerID: string) {
      const previous = inventory();
      const next = {
        ...previous,
        connected: previous.connected.filter((id) => id !== providerID),
      };
      cachedProviders = next;
      writeCachedControlProviders(next);
      providers.mutate(next);
      return previous;
    },
    restoreOptimistic(previous: ControlProviderStore) {
      cachedProviders = previous;
      writeCachedControlProviders(previous);
      providers.mutate(previous);
    },
    refetch: invalidateProviders,
  };
}

type ProviderResources = ReturnType<typeof createAccountProviders>;

const ProviderResourcesContext = createContext<ProviderResources>();

/** The account's providers. */
export function ProviderDataProvider(props: ParentProps) {
  const value = createAccountProviders();
  return createComponent(ProviderResourcesContext.Provider, {
    value,
    get children() {
      return props.children;
    },
  });
}

export function useProviders() {
  const value = useContext(ProviderResourcesContext);
  if (!value)
    throw new Error("useProviders must be used within ProviderDataProvider");
  return value;
}
